import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogTrigger, DialogDescription } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import { toast } from "sonner";
import { apiError } from "@/lib/api";
import { DatePicker, DateTimePicker } from "@/components/DatePicker";

export function FormDialog({ title, description, trigger, fields, initial = {}, onSubmit, submitLabel = "Save", testId }) {
  const [open, setOpen] = useState(false);
  const [values, setValues] = useState(initial);
  const [loading, setLoading] = useState(false);

  const reset = (o) => { setOpen(o); if (o) setValues(initial); };
  const set = (k, v) => setValues((p) => ({ ...p, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    for (const f of fields) {
      if (f.required && !values[f.name]) { toast.error(`${f.label} is required`); return; }
    }
    setLoading(true);
    try {
      await onSubmit(values);
      toast.success(`${title} saved`);
      setOpen(false);
    } catch (ex) {
      toast.error(apiError(ex.response?.data?.detail) || ex.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={reset}>
      <DialogTrigger asChild>{trigger}</DialogTrigger>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid={testId}>
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          {description && <DialogDescription>{description}</DialogDescription>}
        </DialogHeader>
        <form onSubmit={submit} className="grid grid-cols-1 sm:grid-cols-2 gap-4 py-2">
          {fields.map((f) => (
            <div key={f.name} className={f.full ? "sm:col-span-2" : ""}>
              <Label className="text-xs font-semibold text-muted-foreground">{f.label}{f.required && " *"}</Label>
              {f.type === "select" ? (
                <Select value={values[f.name] || ""} onValueChange={(v) => set(f.name, v)}>
                  <SelectTrigger className="mt-1.5" data-testid={`field-${f.name}`}><SelectValue placeholder={f.placeholder || "Select"} /></SelectTrigger>
                  <SelectContent>
                    {f.options.map((o) => (
                      <SelectItem key={o.value ?? o} value={o.value ?? o}>{o.label ?? o}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : f.type === "textarea" ? (
                <Textarea className="mt-1.5" value={values[f.name] || ""} onChange={(e) => set(f.name, e.target.value)} placeholder={f.placeholder} data-testid={`field-${f.name}`} />
              ) : f.type === "date" ? (
                <DatePicker className="mt-1.5 w-full" value={values[f.name] || ""} onChange={(x) => set(f.name, x)} testId={`field-${f.name}`} {...(f.name === "dob" ? { years: [1940, new Date().getFullYear()] } : {})} />
              ) : f.type === "datetime-local" ? (
                <DateTimePicker className="mt-1.5" value={values[f.name] || ""} onChange={(x) => set(f.name, x)} testId={`field-${f.name}`} />
              ) : (
                <Input className="mt-1.5" type={f.type || "text"} value={values[f.name] ?? ""}
                  onChange={(e) => set(f.name, f.type === "number" ? (e.target.value === "" ? "" : Number(e.target.value)) : e.target.value)}
                  placeholder={f.placeholder} data-testid={`field-${f.name}`} />
              )}
            </div>
          ))}
          <DialogFooter className="sm:col-span-2 mt-2">
            <Button type="submit" disabled={loading} data-testid="form-submit-btn">
              {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : submitLabel}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
